import React, { useMemo } from 'react';
import { CurriculumService } from '../../services/curriculumService';
import { EducationData, Achievement } from '../../types/education';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Trophy, 
  Award, 
  Target, 
  TrendingUp, 
  BookOpen, 
  Clock,
  Star,
  Zap,
  Flame,
  CheckCircle
} from 'lucide-react';

interface ProgressDashboardProps {
  curriculumService: CurriculumService;
  educationData: EducationData;
  analytics?: any;
}

export const ProgressDashboard: React.FC<ProgressDashboardProps> = ({
  curriculumService,
  educationData,
  analytics,
}) => {
  // Calculate enhanced progress metrics
  const progressMetrics = useMemo(() => {
    const completionPercentage = educationData.stats.totalLessons > 0
      ? Math.round((educationData.stats.completedLessons / educationData.stats.totalLessons) * 100)
      : 0;

    const averageSessionTime = educationData.stats.completedLessons > 0
      ? Math.round(educationData.stats.totalTimeSpent / educationData.stats.completedLessons)
      : 0;

    const weeklyProgress = calculateWeeklyProgress(educationData);
    const skillBreakdown = calculateSkillBreakdown(curriculumService, educationData);

    return {
      completionPercentage,
      averageSessionTime,
      weeklyProgress,
      skillBreakdown,
      totalHours: Math.round(educationData.stats.totalTimeSpent / 60),
      estimatedTotalHours: curriculumService.getCurriculumStats().estimatedTotalHours,
      remainingHours: Math.max(0, curriculumService.getCurriculumStats().estimatedTotalHours - Math.round(educationData.stats.totalTimeSpent / 60))
    };
  }, [educationData, curriculumService]);

  // Mock achievements data (would come from achievement service)
  const achievements = useMemo(() => [
    {
      id: 'first-lesson',
      title: 'First Steps',
      description: 'Complete your first lesson',
      icon: '🎯',
      category: 'completion' as const,
      unlocked: educationData.stats.completedLessons >= 1,
      unlockedAt: educationData.stats.completedLessons >= 1 ? new Date().toISOString() : undefined
    },
    {
      id: 'week-streak',
      title: 'Week Warrior',
      description: 'Maintain a 7-day learning streak',
      icon: '🔥',
      category: 'streak' as const,
      unlocked: educationData.stats.currentStreak >= 7,
      unlockedAt: educationData.stats.currentStreak >= 7 ? new Date().toISOString() : undefined
    },
    {
      id: 'perfect-score',
      title: 'Perfect Score',
      description: 'Score 100% on any quiz',
      icon: '⭐',
      category: 'mastery' as const,
      unlocked: educationData.stats.averageQuizScore === 100,
      unlockedAt: educationData.stats.averageQuizScore === 100 ? new Date().toISOString() : undefined
    },
    {
      id: 'explorer',
      title: 'Color Explorer',
      description: 'Complete lessons in 3 different topics',
      icon: '🌈',
      category: 'exploration' as const,
      unlocked: Object.keys(educationData.progress).length >= 3,
      unlockedAt: Object.keys(educationData.progress).length >= 3 ? new Date().toISOString() : undefined
    }
  ], [educationData]);

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const lockedAchievements = achievements.filter(a => !a.unlocked);

  return (
    <div className="space-y-6">
      {/* Progress Overview Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Overall Progress</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{progressMetrics.completionPercentage}%</div>
            <Progress value={progressMetrics.completionPercentage} className="mt-2" />
            <p className="text-xs text-muted-foreground mt-1">
              {educationData.stats.completedLessons} of {educationData.stats.totalLessons} lessons
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Learning Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{progressMetrics.totalHours}h</div>
            <p className="text-xs text-muted-foreground">
              {progressMetrics.averageSessionTime} min per lesson average
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Streak</CardTitle>
            <Flame className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{educationData.stats.currentStreak}</div>
            <p className="text-xs text-muted-foreground">
              Longest: {educationData.stats.longestStreak} days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quiz Performance</CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{educationData.stats.averageQuizScore}%</div>
            <p className="text-xs text-muted-foreground">
              Average across all quizzes
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Skills Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Skills Development
          </CardTitle>
          <CardDescription>
            Your proficiency across different color theory skills
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {progressMetrics.skillBreakdown.map((skill, index) => (
              <div key={skill.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{skill.name}</span>
                    <Badge variant="outline" className="text-xs">
                      Level {skill.level}
                    </Badge>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {skill.progress}%
                  </span>
                </div>
                <Progress value={skill.progress} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  {skill.completedTopics} of {skill.totalTopics} topics completed
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Achievements */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Unlocked Achievements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-yellow-500" />
              Achievements Earned
            </CardTitle>
            <CardDescription>
              {unlockedAchievements.length} of {achievements.length} achievements unlocked
            </CardDescription>
          </CardHeader>
          <CardContent>
            {unlockedAchievements.length > 0 ? (
              <div className="space-y-3">
                {unlockedAchievements.map((achievement) => (
                  <div key={achievement.id} className="flex items-center gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <div className="text-2xl">{achievement.icon}</div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{achievement.title}</h4>
                      <p className="text-xs text-muted-foreground">{achievement.description}</p>
                    </div>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Start learning to unlock your first achievement!
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Locked Achievements */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-gray-500" />
              Next Achievements
            </CardTitle>
            <CardDescription>
              Keep learning to unlock these achievements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {lockedAchievements.slice(0, 3).map((achievement) => (
                <div key={achievement.id} className="flex items-center gap-3 p-3 opacity-75">
                  <div className="text-2xl grayscale">{achievement.icon}</div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{achievement.title}</h4>
                    <p className="text-xs text-muted-foreground">{achievement.description}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    Locked
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Learning Insights */}
      {analytics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Learning Insights
            </CardTitle>
            <CardDescription>
              Personalized insights based on your progress
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Strengths</h4>
                {analytics.insights
                  .filter((insight: any) => insight.type === 'strength')
                  .slice(0, 3)
                  .map((insight: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 rounded">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <p className="text-sm">{insight.message}</p>
                    </div>
                  ))}
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Areas for Improvement</h4>
                {analytics.insights
                  .filter((insight: any) => insight.type === 'improvement')
                  .slice(0, 3)
                  .map((insight: any, index: number) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                      <Target className="h-4 w-4 text-blue-500" />
                      <p className="text-sm">{insight.message}</p>
                    </div>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Helper functions
function calculateWeeklyProgress(educationData: EducationData) {
  // This would calculate progress over the last 7 days
  // For now, return mock data
  return {
    thisWeek: 3,
    lastWeek: 2,
    trend: 'up' as const
  };
}

function calculateSkillBreakdown(curriculumService: CurriculumService, educationData: EducationData) {
  const allTopics = curriculumService.getAllTopics();
  const skillGroups = allTopics.reduce((groups, topic) => {
    topic.skills.forEach(skill => {
      if (!groups[skill]) {
        groups[skill] = {
          name: skill,
          totalTopics: 0,
          completedTopics: 0,
          progress: 0
        };
      }
      groups[skill].totalTopics++;
      
      const topicProgress = curriculumService.getTopicProgress(topic.id, educationData.progress);
      if (topicProgress === 100) {
        groups[skill].completedTopics++;
      }
    });
    return groups;
  }, {} as Record<string, any>);

  return Object.values(skillGroups).map((skill: any) => ({
    ...skill,
    progress: Math.round((skill.completedTopics / skill.totalTopics) * 100),
    level: skill.progress >= 80 ? 3 : skill.progress >= 50 ? 2 : 1
  }));
}
